import React from 'react';
import './Collections.css';
import { Availability, SetAvailabilityInput, TimeSlot, WeeklySchedule } from '../../client/core';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS: Record<string, string> = {
    monday: 'Monday',
    tuesday: 'Tuesday',
    wednesday: 'Wednesday',
    thursday: 'Thursday',
    friday: 'Friday',
    saturday: 'Saturday',
    sunday: 'Sunday'
};

const generateTimeOptions = (): string[] => {
    const times: string[] = [];
    for (let hour = 0; hour < 24; hour++) {
        for (let minute = 0; minute < 60; minute += 30) {
            const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
            times.push(timeString);
        }
    }
    return times;
};

const TIME_OPTIONS = generateTimeOptions();

export interface WeeklyHoursProps {
    shopId: string;
    availability: Availability;
    onChange: (availability: Availability) => void;
    onSave?: (input: SetAvailabilityInput) => void;
}

const WeeklyHours: React.FC<WeeklyHoursProps> = ({
    shopId,
    availability,
    onChange,
    onSave
}) => {
    const weekly = availability?.weekly || [];

    const isDayEnabled = (day: string): boolean => {
        return weekly.some(w => w.day === day);
    };

    const getTimesForDay = (day: string): TimeSlot[] => {
        const daySchedule = weekly.find(w => w.day === day);
        return daySchedule?.times || [];
    };

    const toggleDay = (day: string) => {
        if (isDayEnabled(day)) {
            onChange({
                ...availability,
                weekly: weekly.filter(w => w.day !== day)
            });
        } else {
            onChange({
                ...availability,
                weekly: [...weekly, { day: day as WeeklySchedule['day'], times: [] }]
            });
        }
    };

    const addTimeRange = (day: string) => {
        const newWeekly = weekly.map(w => {
            if (w.day === day) {
                return { ...w, times: [...w.times, { start: '09:00', end: '17:00' }] };
            }
            return w;
        });
        onChange({ ...availability, weekly: newWeekly });
    };

    const removeTimeRange = (day: string, index: number) => {
        const newWeekly = weekly.map(w => {
            if (w.day === day) {
                const newTimes = [...w.times];
                newTimes.splice(index, 1);
                return { ...w, times: newTimes };
            }
            return w;
        });
        onChange({ ...availability, weekly: newWeekly });
    };

    const updateTimeRange = (day: string, index: number, field: 'start' | 'end', value: string) => {
        const newWeekly = weekly.map(w => {
            if (w.day === day) {
                const newTimes = w.times.map((t, i) => {
                    if (i === index) {
                        return { ...t, [field]: value };
                    }
                    return t;
                });
                return { ...w, times: newTimes };
            }
            return w;
        });
        onChange({ ...availability, weekly: newWeekly });
    };

    const handleSave = () => {
        if (onSave) {
            onSave({
                shopId,
                type: 'collection',
                availability
            });
        }
    };

    return (
        <div className="collections-container">
            <div className="collection-availability-section">
                <div className="section-header">
                    <h2>Weekly Hours</h2>
                    <button onClick={handleSave} className="save-button">
                        Save Availability
                    </button>
                </div>

                <div className="availability-editor">
                    {DAYS.map(day => (
                        <div key={day} className="day-row">
                            <div className="day-header">
                                <label className="day-toggle">
                                    <input
                                        type="checkbox"
                                        checked={isDayEnabled(day)}
                                        onChange={() => toggleDay(day)}
                                        data-testid={`toggle-${day}`}
                                    />
                                    <span className="day-label">{DAY_LABELS[day]}</span>
                                </label>
                            </div>

                            {isDayEnabled(day) && (
                                <div className="day-content">
                                    <div className="time-ranges">
                                        {getTimesForDay(day).map((timeRange, index) => (
                                            <div key={index} className="time-range" data-testid={`time-range-${day}-${index}`}>
                                                <select
                                                    value={timeRange.start}
                                                    onChange={(e) => updateTimeRange(day, index, 'start', e.target.value)}
                                                    data-testid={`start-time-${day}-${index}`}
                                                    className="time-select"
                                                >
                                                    {TIME_OPTIONS.map(time => (
                                                        <option key={time} value={time}>{time}</option>
                                                    ))}
                                                </select>

                                                <span className="time-separator">-</span>

                                                <select
                                                    value={timeRange.end}
                                                    onChange={(e) => updateTimeRange(day, index, 'end', e.target.value)}
                                                    data-testid={`end-time-${day}-${index}`}
                                                    className="time-select"
                                                >
                                                    {TIME_OPTIONS.map(time => (
                                                        <option key={time} value={time}>{time}</option>
                                                    ))}
                                                </select>

                                                <button
                                                    onClick={() => removeTimeRange(day, index)}
                                                    data-testid={`delete-range-${day}-${index}`}
                                                    className="delete-button"
                                                    aria-label="Remove time range"
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        ))}
                                    </div>

                                    <button
                                        onClick={() => addTimeRange(day)}
                                        data-testid={`add-interval-${day}`}
                                        className="add-interval-button"
                                    >
                                        + Add new interval
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default WeeklyHours;
